import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  useCreateAircraftMetadata,
  useNotifyStaffAircraftMetadata,
  type AircraftMetadataCreate as AircraftMetadataCreateType,
} from "@/queries/landing";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AircraftMetadataForm,
  aircraftMetadataFormSchema,
  type AircraftMetadataFormValues,
} from "./AircraftMetadataForm";

interface CreateAircraftMetadataButtonProps {
  buttonText?: string;
  buttonVariant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const CreateAircraftMetadataButton: React.FC<
  CreateAircraftMetadataButtonProps
> = ({
  buttonText = "Add New Tail Number",
  buttonVariant = "default",
  buttonSize = "default",
  className = "",
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const { mutate: createMetadata, isPending: isCreatingMetadata } =
    useCreateAircraftMetadata();
  const { mutate: notifyStaff, isPending: isNotifyingStaff } =
    useNotifyStaffAircraftMetadata();
  const form = useForm<AircraftMetadataFormValues>({
    resolver: zodResolver(aircraftMetadataFormSchema),
    defaultValues: {
      tail_number: "",
      aircraft_id: undefined,
      carrier_id: undefined,
      general_aviation: false,
      add_new_carrier_mode: false,
      carrier_name: "",
      carrier_code: "",
    },
  });

  useEffect(() => {
    if (!!isModalOpen) {
      form.reset({
        tail_number: "",
        aircraft_id: undefined,
        carrier_id: undefined,
        general_aviation: false,
        add_new_carrier_mode: false,
        carrier_name: "",
        carrier_code: "",
      });
      setSuccess(false);
      setStep(0);
      setError(null);
    }
  }, [isModalOpen, form]);

  const onSubmit = (values: AircraftMetadataFormValues) => {
    const payload: AircraftMetadataCreateType = {
      tail_number: values.tail_number,
      aircraft_id: values.aircraft_id as number,
      general_aviation: values.general_aviation,
      ...(values.general_aviation
        ? {}
        : {
            carrier_id: values.carrier_id,
            carrier_name: values.carrier_name,
            carrier_code: values.carrier_code,
          }),
    };

    createMetadata(payload, {
      onSuccess: () => {
        const notifyPayload = {
          tail_number: values.tail_number,
          aircraft_id: values.aircraft_id as number,
          general_aviation: values.general_aviation,
          new: true,
          ...(values.general_aviation
            ? {}
            : {
                carrier_id: values.carrier_id,
                carrier_name: values.carrier_name,
                carrier_code: values.carrier_code,
              }),
        };
        notifyStaff(notifyPayload, {
          onSuccess: () => {
            setSuccess(true);
            setStep(2);
          },
          onError: (staffNotifyError) => {
            console.error("Staff notification failed:", staffNotifyError);
            setSuccess(true);
            setStep(2);
          },
        });
      },
      onError: (err) => {
        setError(err.message);
        if (err.message.includes("already exists")) {
          setStep(1);
        }
      },
    });
  };

  const handleNotifyExisting = () => {
    const values = form.getValues();
    const notifyPayload = {
      tail_number: values.tail_number,
      aircraft_id: values.aircraft_id as number,
      general_aviation: values.general_aviation,
      new: false,
      ...(values.general_aviation
        ? {}
        : {
            carrier_id: values.carrier_id,
            carrier_name: values.carrier_name,
            carrier_code: values.carrier_code,
          }),
    };
    notifyStaff(notifyPayload, {
      onSuccess: () => {
        setSuccess(false);
        setStep(2);
      },
      onError: (staffNotifyError) => {
        setError(staffNotifyError.message);
      },
    });
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogTrigger asChild>
        <Button variant={buttonVariant} size={buttonSize} className={className}>
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle>Submission successful</DialogTitle>
              <DialogDescription>
                {success
                  ? "The aircraft metadata has been created successfully. Staff has been notified."
                  : "Thank you. Staff has been notified and you will hear from the RadioHub team within 2 business days."}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Button
                variant="default"
                className="w-full"
                onClick={() => setIsModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle>
                Tail Number "{form.getValues("tail_number")}" already exists.
              </DialogTitle>
              <DialogDescription>
                Is the information you entered correct?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 flex gap-2">
              <Button
                variant="default"
                className="w-[70px]"
                disabled={isNotifyingStaff}
                onClick={handleNotifyExisting}
              >
                Yes
              </Button>
              <Button
                variant="outline"
                className="w-[70px]"
                disabled={isNotifyingStaff}
                onClick={() => setStep(0)}
              >
                No
              </Button>
            </div>
          </>
        )}
        {step === 0 && (
          <>
            <DialogHeader>
              <DialogTitle>Add New Aircraft Metadata</DialogTitle>
              <DialogDescription>
                Fill in the details for the new aircraft metadata record. All
                fields are required.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <AircraftMetadataForm
                form={form}
                onSubmit={onSubmit}
                isLoading={isCreatingMetadata || isNotifyingStaff}
                submitButtonText="Create Metadata"
              />
              {error && <div className="mt-4 text-red-500">{error}</div>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
